import { inventoryTargetType, inventoryTrackingMode } from './inventoryUnitModel.js';

export const PACK_GUIDE_COMPONENTS = [
  'Front Pocket',
  'Long Front Pocket',
  'Main Pocket',
  'Yellow Pouch',
  'Red Pouch',
  'Right Pouch',
  'Left Pouch',
  'Green Pouch',
  'Blue Pouch',
];

export const BLS_GUIDE_COMPONENTS = [
  'Front Pocket',
  'Blue Pouch (Bandaging)',
  'Red Pouch (Vitals)',
  'Red Pouch',
  'Zipper Part of Main Med Bag',
  'Side Pouch',
];

export const TRAUMA_GUIDE_COMPONENTS = ['Red Trauma Bag'];
export const O2_GUIDE_COMPONENTS = ['O2 Bag'];
export const AED_GUIDE_COMPONENTS = ['AED'];

function guideComponents(targetId) {
  const type = inventoryTargetType(targetId);
  if (type === 'pack') return PACK_GUIDE_COMPONENTS;
  if (type === 'bls') return BLS_GUIDE_COMPONENTS;
  if (type === 'trauma') return TRAUMA_GUIDE_COMPONENTS;
  if (type === 'o2') return O2_GUIDE_COMPONENTS;
  if (type === 'aed') return AED_GUIDE_COMPONENTS;
  return [];
}

export function inventoryGuideTitle(targetId) {
  const type = inventoryTargetType(targetId);
  if (type === 'pack') return 'Mystery Ranch ALS pack';
  if (type === 'bls') return 'True North BLS pack';
  if (type === 'trauma') return 'Red Trauma Bag';
  if (type === 'o2') return 'O2 Bag';
  if (type === 'aed') return 'AED';
  return '';
}

export function buildInventoryGuideSections(catalog, reconciliation, targetId, expirations = []) {
  const components = guideComponents(targetId);
  if (!components.length) return [];

  const rowByCatalogId = new Map(
    (Array.isArray(reconciliation?.rows) ? reconciliation.rows : [])
      .map(row => [String(row?.catalogId || '').trim(), row]),
  );
  const catalogRows = Array.isArray(catalog) ? catalog : [];

  return components.map((component, index) => {
    const items = catalogRows
      .filter(item => String(item?.component || '').trim() === component)
      .map(item => {
        const row = rowByCatalogId.get(String(item?.catalogId || '').trim()) || {};
        const requiredLabel = String(item?.requiredQty ?? row?.required ?? '—').trim() || '—';
        return {
          ...item,
          required: Number(row?.required || 0),
          requiredLabel,
          found: Number(row?.found || 0),
          complete: Boolean(row?.complete),
          short: Boolean(row?.short),
          expired: Boolean(row?.expired),
          dueSoon: Boolean(row?.dueSoon),
          needsReview: Boolean(row?.needsReview),
          blocker: Boolean(row?.blocker),
          trackingMode: inventoryTrackingMode(item, expirations),
        };
      });

    return {
      id: `${String(targetId || '').trim()}-${index}-${component}`,
      component,
      items,
      complete: items.filter(item => item.complete).length,
      total: items.length,
    };
  }).filter(section => section.items.length > 0);
}

export function inventoryGuideProgress(sections) {
  const items = (Array.isArray(sections) ? sections : []).flatMap(section => section.items || []);
  const complete = items.filter(item => item.complete).length;
  return {
    complete,
    total: items.length,
    remaining: Math.max(0, items.length - complete),
    ready: items.length > 0 && complete === items.length,
  };
}

export function inventoryGuideItemStatus(item) {
  if (item?.expired) return 'EXPIRED';
  if (item?.needsReview) return 'NEEDS REVIEW';
  if (item?.complete && item?.dueSoon) return 'COMPLETE · EXPIRING';
  if (item?.complete) return 'COMPLETE';
  if (Number(item?.found || 0) <= 0) return 'NOT INVENTORIED';
  if (item?.short) return 'SHORT';
  return 'IN PROGRESS';
}

export function nextInventoryGuideItem(sections, currentCatalogId = '') {
  const items = (Array.isArray(sections) ? sections : []).flatMap(section => section.items || []);
  if (!items.length) return null;
  const current = String(currentCatalogId || '').trim();
  const startIndex = current ? items.findIndex(item => String(item?.catalogId || '').trim() === current) : -1;
  const afterCurrent = items.slice(Math.max(0, startIndex + 1)).find(item => !item.complete);
  if (afterCurrent) return afterCurrent;
  return items.find(item => !item.complete) || null;
}
